import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Card,
  TextField,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  LinearProgress,
  Checkbox,
  Divider,
  FormHelperText
} from '@material-ui/core';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import Autocomplete from '@material-ui/lab/Autocomplete';


const RequestPromotionNthElement = ({
  values,
  setFieldValue,
  touched,
  errors
}) => {
  const { storeActiveCategoriesWithAllActiveParents, loadingCategories } = useSelector((state: IReduxState) => state.categories);
  const { store_products: { catalogActiveProducts }, productsLoading } = useSelector((state: IReduxState) => state.products);


  const renderOption = (option, { inputValue, type }) => {
    const optionText = type === 'categories' ? option?.nameWithPath : option.name;
    const matches = match(optionText, inputValue);
    const parts = parse(optionText, matches);

    return (
      <div>
        {parts.map((part, index) => (
          <span key={index} className={part.highlight ? 'text-primary font-bold' : ''}>
            {part.text}
          </span>
        ))}
        {type === 'listings' && Boolean(option.price) && (
          <span className="ml-2 text-green">${option.price}</span>
        )}
      </div>
    );
  };

  return (
    <Card className="p-5 mt-4" elevation={0}>
      <Typography variant="h5" className="font-bold mb-5">
        Applies To <span className="text-error">*</span>
      </Typography>
      <FormControl component="fieldset" className="w-full">
        <RadioGroup
          aria-label="applies to"
          name="everyNth.appliesTo"
          value={values.everyNth.appliesTo.name}
          onChange={(event) => {
            setFieldValue('everyNth.appliesTo.name', event.target.value);
          }}
        >
          <FormControlLabel
            value="Storewide"
            control={<Radio size="small" color="primary" />}
            label="Storewide"
          />
          <FormControlLabel
            value="All Products"
            control={<Radio size="small" color="primary" />}
            label="All Products"
          />
          <FormControlLabel
            value="All Treatments"
            control={<Radio size="small" color="primary" />}
            label="All Treatments"
          />
          <FormControlLabel
            value="Specific Categories/Listings"
            control={
              <Radio
                size="small"
                color="primary"
              />
            }
            label="Specific Categories/Listings"
          />
          {values.everyNth.appliesTo.name === 'Specific Categories/Listings' &&
          <div className="ml-7 flex-column">
            <FormControlLabel
              name="Categories"
              control={
                <Checkbox
                  size="small"
                  color="primary"
                  checked={values.everyNth.appliesTo.isCategories}
                  onChange={(e) => setFieldValue('everyNth.appliesTo.isCategories', e.target.checked)}
                />
              }
              label="Categories"
            />
            {values.everyNth.appliesTo.isCategories &&
            <>
              {loadingCategories ?
                <div className="my-5">
                  <span className="text-muted">Preparing Categories Data...</span>
                  <LinearProgress />
                </div>
                :
                <Autocomplete
                  multiple
                  fullWidth
                  disableClearable
                  size="small"
                  className="my-5"
                  value={values.everyNth.appliesTo.value.categories}
                  options={storeActiveCategoriesWithAllActiveParents}
                  getOptionLabel={(option) => option.nameWithPath}
                  onChange={(e, value) => {
                    setFieldValue(
                      'everyNth.appliesTo.value.categories',
                      value !== null ? value : values.everyNth.appliesTo.value.categories
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="everyNth.appliesTo.value.categories"
                      variant="outlined"
                      label="Categories"
                      required
                      error={touched.everyNth?.appliesTo?.value?.categories && Boolean(errors.everyNth?.appliesTo?.value?.categories)}
                      helperText={touched.everyNth?.appliesTo?.value?.categories && errors.everyNth?.appliesTo?.value?.categories}
                    />
                  )}
                  renderOption={(option, { inputValue }) => renderOption(option, { inputValue, type: 'categories' })}
                />
              }
            </>
            }
            <FormControlLabel
              name="Listings"
              control={
                <Checkbox
                  size="small"
                  color="primary"
                  checked={values.everyNth.appliesTo.isListings}
                  onChange={(e) => setFieldValue('everyNth.appliesTo.isListings', e.target.checked)}
                />
              }
              label="Listings"
            />
            {values.everyNth.appliesTo.isListings &&
            <>
              {productsLoading ?
                <div className="my-5">
                  <span className="text-muted">Preparing Listings Data...</span>
                  <LinearProgress />
                </div>
                :
                <Autocomplete
                  multiple
                  fullWidth
                  disableClearable
                  noOptionsText={
                    <Link
                      to="/request-sku" target="_blank" onMouseDown={() => {
                        window.open('/request-sku', '_blank');
                      }}
                    >
                      Request Listings
                    </Link>
                  }
                  size="small"
                  className="my-5"
                  value={values.everyNth.appliesTo.value.listings}
                  options={catalogActiveProducts}
                  getOptionLabel={(option) => option.name}
                  onChange={(e, value) => {
                    setFieldValue(
                      'everyNth.appliesTo.value.listings',
                      value !== null ? value : values.everyNth.appliesTo.value.listings
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="everyNth.appliesTo.value.listings"
                      variant="outlined"
                      label="Listings"
                      required
                      error={touched.everyNth?.appliesTo?.value?.listings && Boolean(errors.everyNth?.appliesTo?.value?.listings)}
                      helperText={touched.everyNth?.appliesTo?.value?.listings && errors.everyNth?.appliesTo?.value?.listings}
                    />
                  )}
                  renderOption={(option, { inputValue }) => renderOption(option, { inputValue, type: 'listings' })}
                />
              }
            </>
            }
          </div>
          }
          {(touched.everyNth?.appliesTo?.isCategories ||
                        touched.everyNth?.appliesTo?.isListings) &&
                        (Boolean(errors.everyNth?.appliesTo?.isCategories) ||
                        Boolean(errors.everyNth?.appliesTo?.isListings)) &&
                        <FormHelperText>
                          <Typography color="error" variant="caption">
                            At least one needs to be selected
                          </Typography>
                        </FormHelperText>
          }
          {touched.everyNth?.appliesTo?.name && Boolean(errors.everyNth?.appliesTo?.name) &&
          <FormHelperText>
            <Typography color="error" variant="caption">
              {errors.everyNth.appliesTo.name}
            </Typography>
          </FormHelperText>
          }
        </RadioGroup>
      </FormControl>
      <Divider className="my-5" />
      <Typography variant="h5" className="font-bold mb-5">
        Does Not Apply To
      </Typography>
      <FormControl component="fieldset" className="w-full">
        <RadioGroup
          aria-label="not applies to"
          name="everyNth.notAppliesTo"
          value={values.everyNth.notAppliesTo.name}
          onChange={(event) => {
            setFieldValue('everyNth.notAppliesTo.name', event.target.value);
          }}
        >
          <FormControlLabel
            value="None"
            control={<Radio size="small" color="primary" />}
            label="None"
          />
          <FormControlLabel
            value="All Products"
            control={<Radio size="small" color="primary" />}
            label="All Products"
          />
          <FormControlLabel
            value="All Treatments"
            control={<Radio size="small" color="primary" />}
            label="All Treatments"
          />
          <FormControlLabel
            value="Specific Categories/Listings"
            control={
              <Radio
                size="small"
                color="primary"
              />
            }
            label="Specific Categories/Listings"
          />
          {values.everyNth.notAppliesTo.name === 'Specific Categories/Listings' &&
          <div className="ml-7 flex-column">
            <FormControlLabel
              name="Categories"
              control={
                <Checkbox
                  size="small"
                  color="primary"
                  checked={values.everyNth.notAppliesTo.isCategories}
                  onChange={(e) => setFieldValue('everyNth.notAppliesTo.isCategories', e.target.checked)}
                />
              }
              label="Categories"
            />
            {values.everyNth.notAppliesTo.isCategories &&
            <>
              {loadingCategories ?
                <div className="my-5">
                  <span className="text-muted">Preparing Categories Data...</span>
                  <LinearProgress />
                </div>
                :
                <Autocomplete
                  multiple
                  fullWidth
                  disableClearable
                  size="small"
                  className="my-5"
                  value={values.everyNth.notAppliesTo.value.categories}
                  options={storeActiveCategoriesWithAllActiveParents}
                  getOptionLabel={(option) => option.nameWithPath}
                  onChange={(e, value) => {
                    setFieldValue(
                      'everyNth.notAppliesTo.value.categories',
                      value !== null ? value : values.everyNth.notAppliesTo.value.categories
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="everyNth.notAppliesTo.value.categories"
                      variant="outlined"
                      label="Categories"
                      required
                      error={touched.everyNth?.notAppliesTo?.value?.categories && Boolean(errors.everyNth?.notAppliesTo?.value?.categories)}
                      helperText={touched.everyNth?.notAppliesTo?.value?.categories && errors.everyNth?.notAppliesTo?.value?.categories}
                    />
                  )}
                  renderOption={(option, { inputValue }) => renderOption(option, { inputValue, type: 'categories' })}
                />
              }
            </>
            }
            <FormControlLabel
              name="Listings"
              control={
                <Checkbox
                  size="small"
                  color="primary"
                  checked={values.everyNth.notAppliesTo.isListings}
                  onChange={(e) => setFieldValue('everyNth.notAppliesTo.isListings', e.target.checked)}
                />
              }
              label="Listings"
            />
            {values.everyNth.notAppliesTo.isListings &&
            <>
              {productsLoading ?
                <div className="my-5">
                  <span className="text-muted">Preparing Listings Data...</span>
                  <LinearProgress />
                </div>
                :
                <Autocomplete
                  multiple
                  fullWidth
                  disableClearable
                  noOptionsText={
                    <Link
                      to="/request-sku" target="_blank" onMouseDown={() => {
                        window.open('/request-sku', '_blank');
                      }}
                    >
                      Request Listings
                    </Link>
                  }
                  size="small"
                  className="my-5"
                  value={values.everyNth.notAppliesTo.value.listings}
                  options={catalogActiveProducts}
                  getOptionLabel={(option) => option.name}
                  onChange={(e, value) => {
                    setFieldValue(
                      'everyNth.notAppliesTo.value.listings',
                      value !== null ? value : values.everyNth.notAppliesTo.value.listings
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="everyNth.notAppliesTo.value.listings"
                      variant="outlined"
                      label="Listings"
                      required
                      error={touched.everyNth?.notAppliesTo?.value?.listings && Boolean(errors.everyNth?.notAppliesTo?.value?.listings)}
                      helperText={touched.everyNth?.notAppliesTo?.value?.listings && errors.everyNth?.notAppliesTo?.value?.listings}
                    />
                  )}
                  renderOption={(option, { inputValue }) => renderOption(option, { inputValue, type: 'listings' })}
                />
              }
            </>
            }
          </div>
          }
          {(touched.everyNth?.notAppliesTo?.isCategories ||
                        touched.everyNth?.notAppliesTo?.isListings) &&
                        (Boolean(errors.everyNth?.notAppliesTo?.isCategories) ||
                        Boolean(errors.everyNth?.notAppliesTo?.isListings)) &&
                        values.everyNth.notAppliesTo.name === 'Specific Categories/Listings' &&
                        <FormHelperText>
                          <Typography color="error" variant="caption">
                            At least one needs to be selected
                          </Typography>
                        </FormHelperText>
          }
          {touched.everyNth?.notAppliesTo?.name && Boolean(errors.everyNth?.notAppliesTo?.name) &&
          <FormHelperText>
            <Typography color="error" variant="caption">
              {errors.everyNth.notAppliesTo.name}
            </Typography>
          </FormHelperText>
          }
        </RadioGroup>
      </FormControl>
    </Card>
  );
};

export default RequestPromotionNthElement;
